import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import {
  History, Loader2, MoreHorizontal, RotateCw, SquarePen, Sparkles, Trash2, Undo2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetTitle,
} from '@/components/ui/sheet';
import { useToast } from '@/hooks/use-toast';
import { useAssistantPanel } from '@/contexts/AssistantContext';
import { useAssistantConversation } from '@/hooks/use-assistant-conversation';
import { MY_AI_ASSISTANT_CONVERSATIONS } from '@/queries/aiAssistant';
import { mapAssistantError } from '@/lib/ai-assistant/mapAssistantError';
import { useAssistantSuggestions } from '@/hooks/use-assistant-suggestions';
import { isEditorPath } from '@/lib/ai-assistant/clientTools';
import { deriveAssistantActivity } from '@/lib/ai-assistant/deriveAssistantActivity';
import { groupAssistantSteps } from '@/lib/ai-assistant/groupAssistantSteps';
import type { AssistantConversationSummary, AssistantMessage } from '@/lib/ai-assistant/types';
import { AssistantMessageItem } from './AssistantMessageItem';
import { AssistantComposer } from './AssistantComposer';
import { AssistantActivityLine } from './AssistantActivityLine';
import { AssistantConversationSkeleton } from './AssistantConversationSkeleton';
import { AssistantStepGroup } from './AssistantStepGroup';

const DESKTOP_QUERY = '(min-width: 1024px)';

function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true
  ));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setMatches(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return matches;
}

function HistoryMenu({ onPick }: { onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const [load, { data, loading }] = useLazyQuery<{
    myAiAssistantConversations: AssistantConversationSummary[];
  }>(MY_AI_ASSISTANT_CONVERSATIONS, { variables: { limit: 20 } });
  const items = data?.myAiAssistantConversations || [];
  return (
    <DropdownMenu onOpenChange={(isOpen) => { if (isOpen) void load(); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('aiAssistant.history')}>
          <History className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>{t('aiAssistant.history')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading && (
          <div className="space-y-1 py-1" aria-busy="true">
            <Skeleton className="mx-2 my-1.5 h-4" />
            <Skeleton className="mx-2 my-1.5 h-4" />
            <Skeleton className="mx-2 my-1.5 h-4" />
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="px-2 py-3 text-xs text-muted-foreground">{t('aiAssistant.noHistory')}</p>
        )}
        {items.map((item) => (
          <DropdownMenuItem key={item.id} onClick={() => onPick(item.id)} className="truncate">
            <span className="truncate">{item.title || t('aiAssistant.untitled')}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// `docked`: the sidebar beside the page; `sheet`: the mobile sheet (its own
// close button); `studio`: the AI Studio column (always shown, no close).
export function AssistantChat({ variant }: { variant: 'docked' | 'sheet' | 'studio' }) {
  const docked = variant !== 'sheet';
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();
  const {
    setOpen, conversationId, setConversationId, lastEdit, undoLastEdit, dismissLastEdit,
    frameCaptureAvailable, captureCurrentFrame, setStudioChatMounted,
  } = useAssistantPanel();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onError = useCallback((error: unknown) => {
    toast({ title: t(mapAssistantError(error)), variant: 'destructive' });
  }, [t, toast]);

  const {
    conversation, loading, loadError, retry, ownsTurn, working: answering, sending, pending, resolving,
    stopping, reloadSuggested, dismissReload, send, stop, resolveAction, remove,
  } = useAssistantConversation({ conversationId, setConversationId, onError });

  // AI Studio's chat replaces the sidebar while it is on screen. If it goes
  // away mid-turn (the assistant or the user left the studio), the sidebar
  // opens so this tab keeps polling and running the turn's browser calls.
  const turnActiveRef = useRef(false);
  turnActiveRef.current = (sending || answering) && ownsTurn;
  useEffect(() => {
    if (variant !== 'studio') return undefined;
    setStudioChatMounted(true);
    return () => {
      setStudioChatMounted(false);
      if (turnActiveRef.current) setOpen(true);
    };
  }, [variant, setStudioChatMounted, setOpen]);

  const messages = conversation?.messages || [];
  const status = conversation?.status || 'idle';
  // Sending, answering and running editor steps look the same to the user:
  // their message is in the chat and the assistant is working on it.
  const working = sending || answering;
  const activity = deriveAssistantActivity({ sending, conversation, ownsTurn });
  const items = groupAssistantSteps(messages, { inProgress: working });
  const lastMessageId = messages[messages.length - 1]?.id;
  const pendingMessage: AssistantMessage | null = pending ? {
    id: 'pending-user-message',
    role: 'user',
    text: pending.text,
    attachments: pending.attachments,
    tool_name: null,
    tool_title: null,
    tool_status: null,
    action: null,
    effects: [],
    created_at: null,
  } : null;

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [lastMessageId, working, pending, activity.kind]);

  const { suggestions, loading: suggestionsLoading } = useAssistantSuggestions(
    !loading && !loadError && messages.length === 0 && !pendingMessage && !working,
  );

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="assistant-panel" data-variant={variant}>
      {/* AI Studio on a phone shares the screen with the video: tighter chrome. */}
      <div className={`flex shrink-0 items-center gap-1 border-b border-border px-3 ${variant === 'studio' ? 'h-11 lg:h-16' : 'h-16'} ${docked ? '' : 'pr-12'}`}>
        <Sparkles className="h-4 w-4 shrink-0 text-primary" />
        <span className="truncate text-sm font-semibold">{t('aiAssistant.title')}</span>
        <Badge variant="outline" className="ml-1 shrink-0 text-[10px] font-normal">{t('aiAssistant.beta')}</Badge>
        <div className="ml-auto flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setConversationId(null)}
            aria-label={t('aiAssistant.newChat')}
            title={t('aiAssistant.newChat')}
            data-testid="assistant-new-chat"
          >
            <SquarePen className="h-4 w-4" />
          </Button>
          <HistoryMenu onPick={setConversationId} />
          {conversation && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('aiAssistant.more')}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('aiAssistant.deleteConversation')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {variant === 'docked' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setOpen(false)}
              aria-label={t('aiAssistant.close')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-4">
        {loading && !pendingMessage && <AssistantConversationSkeleton />}
        {loadError && (
          <div className="space-y-2 pt-6 text-center text-sm" data-testid="assistant-load-error">
            <p className="text-muted-foreground">{t('aiAssistant.loadError')}</p>
            <Button size="sm" variant="outline" onClick={retry}>{t('aiAssistant.retry')}</Button>
          </div>
        )}
        {!loading && !loadError && messages.length === 0 && !pendingMessage && (
          <div className="space-y-4 pt-6 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-primary" />
            <div className="space-y-1">
              <p className="font-semibold">{t('aiAssistant.emptyTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('aiAssistant.emptyBody')}</p>
            </div>
            <div className="flex flex-col gap-2" aria-busy={suggestionsLoading} data-testid="assistant-suggestions">
              {suggestionsLoading ? (
                <>
                  {/* Starters are generated from the page (and clip): a few seconds. */}
                  <p role="status" className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    {t(isEditorPath(location.pathname)
                      ? 'aiAssistant.suggestionsLoadingClip'
                      : 'aiAssistant.suggestionsLoading')}
                  </p>
                  {['w-4/5', 'w-3/5', 'w-2/3'].map((width) => (
                    <div key={width} className="flex h-10 items-center rounded-md border border-border px-3">
                      <Skeleton className={`h-3 ${width}`} />
                    </div>
                  ))}
                </>
              ) : suggestions.map((text) => (
                <Button
                  key={text}
                  variant="outline"
                  size="sm"
                  className="h-auto whitespace-normal py-2 text-left text-xs font-normal"
                  disabled={working}
                  onClick={() => { void send(text); }}
                >
                  {text}
                </Button>
              ))}
            </div>
          </div>
        )}
        {items.map((item) => (item.type === 'steps' ? (
          <AssistantStepGroup key={item.id} messages={item.messages} failed={item.failed} />
        ) : (
          <AssistantMessageItem
            key={item.message.id}
            message={item.message}
            canResolve={status === 'awaiting_confirmation'}
            resolving={resolving}
            inProgress={working}
            onResolve={(actionId, approve) => { void resolveAction(actionId, approve); }}
          />
        )))}
        {pendingMessage && (
          <AssistantMessageItem
            message={pendingMessage}
            canResolve={false}
            resolving={false}
            onResolve={() => {}}
          />
        )}
        <AssistantActivityLine activity={activity} />
        {!working && conversation?.error === 'interrupted' && (
          <p className="px-1 text-xs text-muted-foreground">{t('aiAssistant.interrupted')}</p>
        )}
        {!working && conversation?.error === 'stopped' && (
          <p className="px-1 text-xs text-muted-foreground">{t('aiAssistant.stopped')}</p>
        )}
      </div>

      {lastEdit && lastEdit.conversationId === conversation?.id && !working && (
        <div
          className="mx-3 mb-2 flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 p-2 text-xs"
          data-testid="assistant-undo-edit"
        >
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="flex-1">{t('aiAssistant.editApplied')}</span>
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={() => {
              undoLastEdit();
              toast({ title: t('aiAssistant.editUndone') });
            }}
          >
            <Undo2 className="h-3.5 w-3.5" />
            {t('aiAssistant.undoEdit')}
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={dismissLastEdit} aria-label={t('aiAssistant.close')}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {reloadSuggested && (
        <div className="mx-3 mb-2 flex items-center gap-2 rounded-md border border-border bg-muted/50 p-2 text-xs">
          <span className="flex-1">{t('aiAssistant.reloadNotice')}</span>
          <Button size="sm" variant="outline" className="h-7" onClick={() => window.location.reload()}>
            <RotateCw className="h-3.5 w-3.5" />
            {t('aiAssistant.reload')}
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={dismissReload} aria-label={t('aiAssistant.close')}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className={`shrink-0 border-t border-border ${variant === 'studio' ? 'p-2 lg:p-3' : 'p-3'}`}>
        <AssistantComposer
          disabled={working}
          onSend={send}
          onStop={() => { void stop(); }}
          stopping={stopping}
          onCaptureFrame={frameCaptureAvailable ? captureCurrentFrame : undefined}
        />
        <p className="mt-2 text-[10px] leading-tight text-muted-foreground">{t('aiAssistant.disclaimer')}</p>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('aiAssistant.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('aiAssistant.deleteBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('aiAssistant.action.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { void remove(); }}>
              {t('aiAssistant.deleteConversation')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Right-hand assistant sidebar. Docked beside the page from `lg`, a sheet
// below. Rendered by Layout only for users with the `ai_assistant` feature.
export function AssistantPanel() {
  const { t } = useTranslation();
  const { open, setOpen, setPanelMounted } = useAssistantPanel();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    setPanelMounted(true);
    return () => setPanelMounted(false);
  }, [setPanelMounted]);

  if (isDesktop) {
    if (!open) return null;
    return (
      <aside
        aria-label={t('aiAssistant.title')}
        className="sticky top-0 h-screen w-[380px] shrink-0 border-l border-border bg-card"
      >
        <AssistantChat variant="docked" />
      </aside>
    );
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full p-0 sm:max-w-md">
        <SheetTitle className="sr-only">{t('aiAssistant.title')}</SheetTitle>
        <SheetDescription className="sr-only">{t('aiAssistant.emptyBody')}</SheetDescription>
        <AssistantChat variant="sheet" />
      </SheetContent>
    </Sheet>
  );
}
